import { endpoints } from '@/api/endpoints';
import {
  createApplicationResponseSchema,
  documentsResponseSchema,
  getApplicationResponseSchema,
  getCreditTierResponseSchema,
  productTierSchema,
} from '@/api/UK/credit/schemas';
import type { DocumentName } from '@/api/UK/credit/types';
import { apiRequest } from '@/api/utils/api';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- needs raw AxiosResponse for content-disposition header
import axiosInstance from '@/api/utils/axiosInstance';
import type { UtmTagData } from '@/utils/tracking';

export * from '@/api/UK/credit/types';

type CreateApplicationPayload = {
  applicantId: string;
  purchaseTrackingId?: string;
  utm?: UtmTagData;
};

export const getCreditAmount = () =>
  apiRequest({
    url: endpoints.UK.credit.amount,
    method: 'GET',
    schema: getCreditTierResponseSchema,
  });

export const getApplication = (applicationId: string) =>
  apiRequest({
    url: `/uk/credit/applications/${applicationId}`,
    method: 'GET',
    schema: getApplicationResponseSchema,
  });

export const createApplication = (payload: CreateApplicationPayload) =>
  apiRequest({
    url: '/uk/credit/applications',
    method: 'POST',
    schema: createApplicationResponseSchema,
    data: payload,
  });

export const selectProductTier = (applicationId: string, productTierId: string) =>
  apiRequest<''>({
    url: `/uk/credit/applications/${applicationId}/product-tier/${productTierId}`,
    method: 'POST',
  });

export const getProductTierProjection = (productTierId: string, installments: number) =>
  apiRequest({
    url: endpoints.UK.credit.productTierProjection(productTierId),
    method: 'POST',
    data: { installments },
    schema: productTierSchema,
  });

export const getDocuments = (applicationId: string) =>
  apiRequest({
    url: `/uk/credit/applications/${applicationId}/documents`,
    method: 'GET',
    headers: { Accept: 'application/json' },
    schema: documentsResponseSchema,
  });

export const getDocumentFile = async (applicationId: string, documentName: DocumentName) => {
  // Needs full AxiosResponse for content-disposition header — cannot use api wrapper
  const response = await axiosInstance.get<Blob>(
    `/uk/credit/applications/${applicationId}/documents/${documentName}`,
    { headers: { Accept: 'application/pdf' }, responseType: 'blob' },
  );
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const contentDisposition: string = response.headers['content-disposition'] ?? '';
  const fileName = /filename="(.*)"/.exec(contentDisposition)?.[1] ?? `${documentName}.pdf`;
  return { data: response.data, fileName };
};

export const signDocuments = (applicationId: string, documents: DocumentName[]) =>
  apiRequest<''>({
    url: `/uk/credit/applications/${applicationId}/documents`,
    method: 'POST',
    data: { documents },
  });

export const finalizeApplication = (applicationId: string) =>
  apiRequest<''>({
    url: endpoints.UK.credit.applications.applicationId(applicationId).finalize,
    method: 'POST',
  });
